import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, FileText, Upload, Download, Trash2, AlertTriangle, CheckCircle2, Clock, Users, FolderOpen, Calendar as CalendarIcon, CalendarClock, Briefcase, Search, Workflow } from "lucide-react";
import WorkflowBuilder from "@/components/WorkflowBuilder";
import WorkerSheet from "@/components/WorkerSheet";
import { useConfirm } from "@/components/ConfirmProvider";
import MandanteLogo from "@/components/MandanteLogo";
import DocumentPreviewModal from "@/components/DocumentPreviewModal";
import JornadasSection from "@/components/JornadasSection";
import ContratoCalendarDialog from "@/components/ContratoCalendarDialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function AcreditacionContrato() {
  const confirmAction = useConfirm();
  const { id: mid, cid } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [mandante, setMandante] = useState(null);
  const [contrato, setContrato] = useState(null);
  const [docs, setDocs] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [uploadingId, setUploadingId] = useState(null);
  const [observeDoc, setObserveDoc] = useState(null);
  const [observeText, setObserveText] = useState("");
  const [openWorker, setOpenWorker] = useState(null);
  const [preview, setPreview] = useState(null); // {items: [...], index: number}
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [cargos, setCargos] = useState([]);
  const [wQuery, setWQuery] = useState("");
  const [wCargo, setWCargo] = useState("");
  const fileInputs = useRef({});
  const canEdit = ["ADMIN", "HR_MANAGER", "RECRUITER", "SUPERVISOR"].includes(user?.role);
  const canConfigure = ["ADMIN", "HR_MANAGER"].includes(user?.role);

  const load = async () => {
    try {
      const [m, c, d, w, cg] = await Promise.all([
        api.get("/mandantes").then((r) => r.data.find((x) => x.id === mid)),
        api.get(`/mandantes/${mid}/contratos/${cid}`).then((r) => r.data),
        api.get(`/mandantes/${mid}/contratos/${cid}/documentos`).then((r) => r.data),
        api.get("/workers").then((r) => r.data),
        api.get(`/mandantes/${mid}/contratos/${cid}/cargos`).then((r) => r.data),
      ]);
      if (!m || !c) { toast.error("Contrato no encontrado"); nav("/acreditacion"); return; }
      setMandante(m); setContrato(c); setDocs(d); setCargos(cg);
      setWorkers(w.filter((x) => (x.mandante || x.company) === m.name && x.contrato === c.name));
    } catch (e) { toast.error("No se pudo cargar"); }
  };
  useEffect(() => { load(); }, [mid, cid]);

  const triggerUpload = (d) => fileInputs.current[d.id]?.click();
  const uploadFile = async (d, file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) { toast.error("Solo PDF"); return; }
    setUploadingId(d.id);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/mandantes/${mid}/contratos/${cid}/documentos/${d.id}/file`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDocs(docs.map((x) => x.id === d.id ? { ...x, ...data } : x));
      toast.success(`Documento cargado: ${d.name}`);
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo subir"); }
    finally { setUploadingId(null); }
  };
  const removeFile = async (d) => {
    const ok = await confirmAction({ title: `Eliminar archivo "${d.name}"`,
      description: "El PDF subido se eliminará. El estándar del contrato se mantiene y puedes volver a cargarlo.",
      confirmText: "Eliminar archivo", variant: "danger" });
    if (!ok) return;
    try {
      const { data } = await api.delete(`/mandantes/${mid}/contratos/${cid}/documentos/${d.id}/file`);
      setDocs(docs.map((x) => x.id === d.id ? { ...x, ...data } : x));
      toast.success("Archivo eliminado");
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo eliminar"); }
  };
  const downloadFile = (d) => {
    const uploaded = docs.filter((x) => x.storage_path);
    const items = uploaded.map((x) => ({
      url: `/mandantes/${mid}/contratos/${cid}/documentos/${x.id}/file`,
      title: x.name,
      filename: x.original_filename || `${x.name}.pdf`,
    }));
    const idx = uploaded.findIndex((x) => x.id === d.id);
    setPreview({ items, index: idx < 0 ? 0 : idx });
  };
  const saveObservation = async () => {
    if (!observeDoc) return;
    try {
      const { data } = await api.patch(`/mandantes/${mid}/contratos/${cid}/documentos/${observeDoc.id}/status`, {
        status: observeText.trim() ? "OBSERVED" : (observeDoc.storage_path ? "UPLOADED" : "EMPTY"),
        observation: observeText,
      });
      setDocs(docs.map((x) => x.id === observeDoc.id ? { ...x, ...data } : x));
      setObserveDoc(null); setObserveText("");
      toast.success("Observación guardada");
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo guardar"); }
  };

  const formatDateTime = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    } catch (e) { return "—"; }
  };

  if (!contrato || !mandante) return <div className="text-sm text-slate-500">Cargando…</div>;

  const riskColor = (p) => p >= 80 ? "bg-emerald-500" : p >= 50 ? "bg-amber-500" : "bg-red-500";
  const uploaded = docs.filter((d) => d.status === "UPLOADED").length;
  const observed = docs.filter((d) => d.status === "OBSERVED").length;
  const empty = docs.length - uploaded - observed;

  return (
    <div className="space-y-6">
      <nav className="flex items-center gap-2 text-sm text-slate-500 flex-wrap">
        <Link to="/acreditacion" className="hover:text-emerald-600 inline-flex items-center gap-1"><ChevronLeft size={14} /> Acreditación</Link>
        <ChevronRight size={14} />
        <Link to={`/acreditacion/${mid}`} className="hover:text-emerald-600">{mandante.name}</Link>
        <ChevronRight size={14} />
        <span className="text-slate-700 font-medium">{contrato.name}</span>
      </nav>

      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <MandanteLogo mandante={mandante} size="lg" />
          <div>
            <h1 className="font-display text-3xl font-bold text-slate-900">{contrato.name}</h1>
            <p className="text-slate-500 text-sm mt-1">{mandante.name}{contrato.numero_contrato ? ` · N° ${contrato.numero_contrato}` : ""}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="open-contrato-calendar" onClick={() => setCalendarOpen(true)}
            className="inline-flex items-center gap-1.5 border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 text-slate-700 text-xs font-semibold px-3 py-1.5 rounded-lg">
            <CalendarIcon size={14} /> Calendario del mes
          </button>
          <Link to={`/mandantes/${mid}/contratos/${cid}`} className="inline-flex items-center gap-1.5 border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 text-slate-700 text-xs font-semibold px-3 py-1.5 rounded-lg">
            Ir a configuración
          </Link>
        </div>
      </div>

      {/* Pestañas de acreditación del contrato */}
      <Tabs defaultValue="contrato" className="w-full">
        <TabsList data-testid="acr-tabs" className="bg-slate-100 h-10 p-1 rounded-xl">
          <TabsTrigger data-testid="acr-tab-contrato" value="contrato" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 px-4">
            <Briefcase size={14} className="mr-1.5" /> Contrato
          </TabsTrigger>
          <TabsTrigger data-testid="acr-tab-jornadas" value="jornadas" className="data-[state=active]:bg-white data-[state=active]:text-indigo-700 px-4">
            <CalendarClock size={14} className="mr-1.5" /> Jornadas
          </TabsTrigger>
          <TabsTrigger data-testid="acr-tab-carpeta" value="carpeta" className="data-[state=active]:bg-white data-[state=active]:text-emerald-700 px-4">
            <FileText size={14} className="mr-1.5" /> Carpeta del contrato
          </TabsTrigger>
          <TabsTrigger data-testid="acr-tab-trabajadores" value="trabajadores" className="data-[state=active]:bg-white data-[state=active]:text-blue-700 px-4">
            <Users size={14} className="mr-1.5" /> Trabajadores <span className="ml-1 text-[10px] font-semibold text-slate-400">({workers.length})</span>
          </TabsTrigger>
          <TabsTrigger data-testid="acr-tab-timeline" value="timeline" className="data-[state=active]:bg-white data-[state=active]:text-indigo-700 px-4">
            <Workflow size={14} className="mr-1.5" /> Timeline
          </TabsTrigger>
        </TabsList>

        {/* Tab: Datos del contrato */}
        <TabsContent value="contrato" className="mt-4">
          <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <h2 className="font-display font-semibold text-slate-800 flex items-center gap-2 mb-4"><Briefcase size={18} className="text-slate-600" /> Datos del contrato</h2>
            <dl className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <DataField label="Glosa" value={contrato.name} />
              <DataField label="N° Contrato" value={contrato.numero_contrato} mono />
              <DataField label="Dotación" value={contrato.dotacion ?? "—"} mono />
              <DataField label="Fecha de inicio" value={formatDateDMY(contrato.fecha_inicio)} />
              <DataField label="Fecha de término" value={formatDateDMY(contrato.fecha_termino)} />
              <DataField label="Gerencia" value={contrato.gerencia} />
              <DataField label="Mandante" value={mandante.name} />
              <DataField label="Trabajadores" value={`${workers.length} asignado(s)`} />
            </dl>
          </section>
        </TabsContent>

        {/* Tab: Jornadas */}
        <TabsContent value="jornadas" className="mt-4">
          <JornadasSection mid={mid} cid={cid} canEdit={canConfigure} cargos={cargos.map((c) => c.name)} />
        </TabsContent>

        {/* Tab: Carpeta del contrato */}
        <TabsContent value="carpeta" className="mt-4">
      <section className="bg-white rounded-2xl border border-slate-200 shadow-sm">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <div>
            <h2 className="font-display font-semibold text-slate-800 flex items-center gap-2"><FileText size={18} className="text-emerald-600" /> Carpeta del contrato</h2>
            <p className="text-xs text-slate-400 mt-0.5">Archivos compliance del propio contrato.</p>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <Chip color="emerald" icon={CheckCircle2} label={`${uploaded} cargado(s)`} />
            <Chip color="amber" icon={AlertTriangle} label={`${observed} observado(s)`} />
            <Chip color="slate" icon={Clock} label={`${empty} pendiente(s)`} />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table data-testid="carpeta-contrato-table" className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-[10px] uppercase tracking-wide text-slate-500 border-b border-slate-100">
                <th className="px-5 py-2">Documento</th>
                <th className="px-5 py-2 whitespace-nowrap">Estado</th>
                <th className="px-5 py-2">Archivo</th>
                <th className="px-5 py-2 whitespace-nowrap">Subido por</th>
                <th className="px-5 py-2 whitespace-nowrap">Fecha</th>
                <th className="px-5 py-2 text-right w-40">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => {
                const uploading = uploadingId === d.id;
                const hasFile = !!d.storage_path;
                return (
                  <tr key={d.id} data-testid={`doc-row-${d.id}`} className="border-t border-slate-50">
                    <td className="px-5 py-3 font-semibold text-slate-800">{d.name}</td>
                    <td className="px-5 py-3"><StatusBadge status={d.status || "EMPTY"} obs={d.observation} /></td>
                    <td className="px-5 py-3">
                      {hasFile ? (
                        <button data-testid={`view-file-${d.id}`} onClick={() => downloadFile(d)} className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline">
                          <Download size={13} /> {d.original_filename || "archivo.pdf"}
                        </button>
                      ) : <span className="text-xs text-slate-300">—</span>}
                    </td>
                    <td className="px-5 py-3 text-xs text-slate-600">{d.uploaded_by || "—"}</td>
                    <td className="px-5 py-3 text-xs text-slate-600 whitespace-nowrap">{formatDateTime(d.uploaded_at)}</td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      <input ref={(el) => { fileInputs.current[d.id] = el; }} type="file" accept="application/pdf" className="hidden"
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(d, f); e.target.value = ""; }} />
                      {canEdit && (
                        <>
                          <button data-testid={`upload-${d.id}`} onClick={() => triggerUpload(d)} disabled={uploading}
                            className="inline-flex items-center gap-1 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white px-2.5 py-1 rounded-md mr-1">
                            <Upload size={12} /> {uploading ? "…" : (hasFile ? "Reemplazar" : "Subir")}
                          </button>
                          <button data-testid={`observe-${d.id}`} onClick={() => { setObserveDoc(d); setObserveText(d.observation || ""); }}
                            className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 hover:bg-amber-100 px-2 py-1 rounded-md mr-1">
                            <AlertTriangle size={12} /> Observar
                          </button>
                          {hasFile && (
                            <button data-testid={`del-file-${d.id}`} onClick={() => removeFile(d)} className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
              {docs.length === 0 && <tr><td colSpan={6} className="px-5 py-4 text-sm text-slate-400 italic">Aún no hay estándares de documentos. Configúralos en <Link to={`/mandantes/${mid}/contratos/${cid}`} className="text-indigo-600 hover:underline">Mandantes</Link>.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
        </TabsContent>

        {/* Tab: Trabajadores */}
        <TabsContent value="trabajadores" className="mt-4">
      <section className="bg-white rounded-2xl border border-slate-200 shadow-sm" data-testid="acr-trabajadores-section">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-blue-50 inline-flex items-center justify-center">
              <Users size={18} className="text-blue-600" />
            </div>
            <div>
              <h2 className="font-display font-semibold text-slate-800">Trabajadores del contrato <span className="text-sm font-normal text-slate-400">({workers.length})</span></h2>
              <p className="text-xs text-slate-400 mt-0.5">Revisa expedientes y carga documentos individuales.</p>
            </div>
          </div>
          <Link to={`/acreditacion/${mid}/contratos/${cid}/trabajadores`} data-testid="go-to-trabajadores"
            className="inline-flex items-center gap-1.5 border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 text-slate-700 text-xs font-semibold px-3 py-1.5 rounded-lg">
            Vista completa <ChevronRight size={14} />
          </Link>
        </div>

        <div className="flex flex-wrap items-center gap-3 px-5 py-3 border-b border-slate-100">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="acr-trab-search" value={wQuery} onChange={(e) => setWQuery(e.target.value)}
              placeholder="Buscar por nombre o RUT…"
              className="w-full h-9 pl-9 pr-3 rounded-lg border border-slate-200 text-sm focus:border-indigo-400 outline-none" />
          </div>
          <select data-testid="acr-trab-filter-cargo" value={wCargo} onChange={(e) => setWCargo(e.target.value)}
            className="h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white">
            <option value="">Todos los cargos</option>
            {Array.from(new Set(workers.map((w) => (w.position || "").trim()).filter(Boolean))).sort().map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        {(() => {
          const q = wQuery.trim().toLowerCase();
          const filtered = workers.filter((w) => {
            const hay = `${w.first_name || ""} ${w.last_name || ""} ${w.rut || ""}`.toLowerCase();
            const posOk = !wCargo || (w.position || "").trim().toUpperCase() === wCargo.toUpperCase();
            return posOk && (!q || hay.includes(q));
          });
          if (filtered.length === 0) {
            return (
              <p className="p-10 text-center text-sm text-slate-400 italic">
                {workers.length === 0 ? "Aún no hay trabajadores en este contrato." : "Sin resultados para los filtros aplicados."}
              </p>
            );
          }
          const riskColor = (p) => p >= 80 ? "bg-emerald-500" : p >= 50 ? "bg-amber-500" : "bg-red-500";
          return (
            <div className="overflow-x-auto">
              <table data-testid="acr-workers-table" className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-left text-[10px] uppercase tracking-wide text-slate-500 border-b border-slate-100">
                    <th className="px-5 py-2 whitespace-nowrap">RUT</th>
                    <th className="px-5 py-2">Trabajador</th>
                    <th className="px-5 py-2">Cargo</th>
                    <th className="px-5 py-2 text-right whitespace-nowrap">Avance</th>
                    <th className="px-5 py-2 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((w) => (
                    <tr key={w.id} data-testid={`acr-worker-row-${w.id}`}
                      className="border-t border-slate-50 hover:bg-blue-50/40 cursor-pointer"
                      onClick={() => nav(`/acreditacion/${mid}/contratos/${cid}/trabajadores/${w.id}`)}>
                      <td className="px-5 py-3 font-mono text-[11px] text-slate-700 whitespace-nowrap">{w.rut || "—"}</td>
                      <td className="px-5 py-3 font-semibold text-slate-800">{w.first_name} {w.last_name}</td>
                      <td className="px-5 py-3 text-slate-600 text-xs">{w.position || "—"}</td>
                      <td className="px-5 py-3 text-right">
                        <div className="inline-flex items-center gap-2">
                          <div className="h-1.5 w-20 bg-slate-100 rounded-full overflow-hidden">
                            <div className={`h-full ${riskColor(w.progress_percentage || 0)}`} style={{ width: `${w.progress_percentage || 0}%` }} />
                          </div>
                          <span className="font-semibold text-blue-600 w-10 text-right">{w.progress_percentage || 0}%</span>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-right"><ChevronRight size={14} className="text-slate-300" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })()}
      </section>
        </TabsContent>

        {/* Tab: Timeline del proceso para este contrato (asignar workers a etapa) */}
        <TabsContent value="timeline" className="mt-4" data-testid="acr-timeline-section">
          {mandante && contrato && (
            <WorkflowBuilder mandanteId={mid} mandanteName={mandante.name} contratoName={contrato.name}
              canEdit={false} canAssign={canEdit} />
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!observeDoc} onOpenChange={(v) => { if (!v) { setObserveDoc(null); setObserveText(""); } }}>
        <DialogContent className="bg-white sm:max-w-[480px]">
          <DialogHeader><DialogTitle className="font-display">Observación · {observeDoc?.name}</DialogTitle></DialogHeader>
          <textarea value={observeText} onChange={(e) => setObserveText(e.target.value)} placeholder="Describe la observación…"
            rows={5} className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-amber-400 outline-none text-sm" />
          <p className="text-[11px] text-slate-500">Vacío → se limpia la observación y el estado vuelve a {observeDoc?.storage_path ? "Cargado" : "Pendiente"}.</p>
          <DialogFooter>
            <button onClick={() => { setObserveDoc(null); setObserveText(""); }} className="text-sm text-slate-600 px-3 py-1.5 rounded-md">Cancelar</button>
            <button onClick={saveObservation} className="bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold px-3 py-1.5 rounded-md">Guardar</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DocumentPreviewModal open={!!preview} onClose={() => setPreview(null)}
        items={preview?.items} index={preview?.index}
        onIndexChange={(i) => setPreview((p) => ({ ...p, index: i }))} />

      {calendarOpen && (
        <ContratoCalendarDialog mid={mid} cid={cid}
          contratoName={contrato.name}
          onClose={() => setCalendarOpen(false)} />
      )}
    </div>
  );
}

function Chip({ color, icon: Icon, label }) {
  const map = {
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    slate: "bg-slate-100 text-slate-600",
  };
  return <span className={`inline-flex items-center gap-1 font-semibold px-2 py-0.5 rounded-full ${map[color]}`}><Icon size={11} /> {label}</span>;
}

function StatusBadge({ status, obs }) {
  const map = {
    UPLOADED: { cls: "bg-emerald-100 text-emerald-700", label: "Cargado" },
    OBSERVED: { cls: "bg-amber-100 text-amber-700", label: "Observado" },
    EMPTY: { cls: "bg-slate-100 text-slate-500", label: "Pendiente" },
  };
  const s = map[status] || map.EMPTY;
  return (
    <span title={obs || ""} className={`inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full ${s.cls}`}>
      {s.label}{status === "OBSERVED" && obs ? " ⚠" : ""}
    </span>
  );
}

function DataField({ label, value, mono }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-wide text-slate-400 font-medium">{label}</dt>
      <dd className={`mt-0.5 text-sm text-slate-800 ${mono ? "font-mono" : ""}`}>{value || <span className="text-slate-300">—</span>}</dd>
    </div>
  );
}

function formatDateDMY(iso) {
  if (!iso) return "—";
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split("-");
  return y && m && d ? `${d}-${m}-${y}` : s;
}
